import {
  DisplayPriceRange,
  Product,
  ProductCardPriceRange,
  TransformedDisplayPriceRange,
  UnitPriceInfo,
} from "../../types/types";
import { PricePerUnit, PriceRange } from "@~/types/prodcatBaseTypes";

const MINIMUM_COST_OF_MADE_TO_MEASURE_SERVICE = 32; // In pounds
const MINIMUM_LENGTH_OF_FABRIC = 0.7; // In metres

export const removeCurrencySymbol = (price: string): string =>
  price.replace("£", "");

export const determinePriceRange = (
  display: DisplayPriceRange,
): TransformedDisplayPriceRange => {
  if (typeof display === "string") return display;

  if (display.max) {
    return display.max === display.min
      ? removeCurrencySymbol(display.max)
      : {
          from: removeCurrencySymbol(display.min),
          to: removeCurrencySymbol(display.max),
        };
  }

  return "";
};

export const transformVariantPriceRange = (
  variantPriceRange: PriceRange,
): ProductCardPriceRange => {
  const { value, reductionHistory } = variantPriceRange;

  const price: ProductCardPriceRange = {
    was: "",
    then1: "",
    then2: "",
    now: determinePriceRange(value ?? ""),
  };

  if (reductionHistory) {
    if (reductionHistory[0]?.display)
      price.was = determinePriceRange(reductionHistory[0].display);
    if (reductionHistory[1]?.display)
      price.then1 = determinePriceRange(reductionHistory[1].display);
    if (reductionHistory[2]?.display)
      price.then2 = determinePriceRange(reductionHistory[2].display);
  }

  return price;
};

export const isPriceAtLeastMinimumThreshold = (
  price: ProductCardPriceRange | null | undefined,
): boolean => {
  if (!price) return false;

  // Some special order items have a non-fixed price that is initially keyed as a
  // value below 5p and then updated to the correct price once the order is placed.
  // We want to hide the price of these products on the PLP.
  return price.now >= "0.05";
};

export const transformBlindPrice = (
  price: ProductCardPriceRange,
): ProductCardPriceRange | object => {
  const calcMinimumBlindPrice = (pricePerMetre: string): string => {
    if (Number.isNaN(Number(pricePerMetre))) {
      return pricePerMetre;
    }
    return (
      Math.round(
        Math.ceil(
          100 *
            (MINIMUM_COST_OF_MADE_TO_MEASURE_SERVICE +
              MINIMUM_LENGTH_OF_FABRIC * Number(pricePerMetre)),
        ),
      ) / 100
    ).toString();
  };

  const { now } = price;
  const priceToUse = typeof now === "string" ? now : now.from;
  return {
    ...price,
    now: calcMinimumBlindPrice(priceToUse),
  };
};

export function getCreditMessage(
  price: ProductCardPriceRange,
  product: Product,
): string | undefined {
  if (!product.credit) return undefined;

  if (typeof price.now === "string" || !price.now.from) {
    if (product.credit.illustration) {
      const { financialProductType, repaymentAmount, creditRate } =
        product.credit.illustration;

      if (financialProductType === "IBC") {
        return `From ${repaymentAmount.display} a month ${creditRate.display} APR*`;
      }
      if (financialProductType === "IFC") {
        return `From ${repaymentAmount.display} a month interest free*`;
      }
    }
  } else if (product.credit.available) {
    return "Payment plans available";
  }

  return undefined;
}

export const transformPricePerUnit = (
  pricePerUnit: PricePerUnit | undefined,
): UnitPriceInfo | null => {
  if (!pricePerUnit) return null;

  const { quantity, unitOfMeasure, display } = pricePerUnit;
  return {
    pricePerUnit: {
      currency: "GBP",
      price: Number(removeCurrencySymbol(display)),
    },
    unit: {
      measure: unitOfMeasure,
      quantity: Number(quantity),
    },
  };
};
